/**
 * Sprint 24.5 — InstallStepToolsets (wizard step 3).
 *
 * Toolset selection for templates that declare toolsets (M365). Defaults come
 * pre-checked (Q10); the live count updates as the user toggles. The modal skips
 * this step entirely when the template has no toolsets.
 */

import type { CSSProperties } from "react";
import type { FirstPartyTemplate } from "../../../../hooks/use-first-party-templates";
import { ToolsetCheckboxRow } from "./ToolsetCheckboxRow";

export interface InstallStepToolsetsProps {
  template: FirstPartyTemplate;
  selected: string[];
  onToggle: (toolsetId: string) => void;
  onContinue: () => void;
  busy?: boolean;
}

export function InstallStepToolsets({
  template,
  selected,
  onToggle,
  onContinue,
  busy,
}: InstallStepToolsetsProps) {
  const toolsets = template.toolsets ?? [];
  const selectedSet = new Set(selected);
  const liveCount = toolsets
    .filter((t) => selectedSet.has(t.id))
    .reduce((sum, t) => sum + t.tool_count_estimate, 0);

  return (
    <div style={body}>
      <div>
        <h3 style={h3}>Pick toolsets</h3>
        <p style={sub}>Enable the {template.display_name} areas your hires need.</p>
      </div>

      <div style={list}>
        {toolsets.map((ts) => (
          <ToolsetCheckboxRow
            key={ts.id}
            toolset={ts}
            checked={selectedSet.has(ts.id)}
            onToggle={() => onToggle(ts.id)}
          />
        ))}
      </div>

      <div style={liveCountStyle}>
        ~{liveCount} tool{liveCount === 1 ? "" : "s"} will be enabled
      </div>

      <div style={footer}>
        <button type="button" style={primaryBtn} onClick={onContinue} disabled={busy}>
          {busy ? "Saving…" : "Continue →"}
        </button>
      </div>
    </div>
  );
}

const body: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  padding: "24px 28px",
  fontFamily: "var(--font-sans)",
};

const h3: CSSProperties = { fontSize: 16, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" };
const sub: CSSProperties = { fontSize: 12, color: "var(--color-text-secondary)", margin: "4px 0 0" };

const list: CSSProperties = { display: "flex", flexDirection: "column", gap: 6 };

const liveCountStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  fontWeight: 500,
};

const footer: CSSProperties = { display: "flex", justifyContent: "flex-end", marginTop: 4 };

const primaryBtn: CSSProperties = {
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-text-primary)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
